import React from 'react';

interface WorkloadStats {
  id: string;
  name: string;
  status: string;
  totalAddresses: number;
  completedAddresses: number;
  pendingAddresses: number;
  notHomeAddresses: number;
  completionRate: number;
  region?: string;
  assignedTo: string[];
}

interface RegionSummary {
  region: string;
  workloadCount: number;
  totalHomes: number;
  completedHomes: number;
  completionRate: number;
}

interface StatusDistribution {
  status: string;
  count: number;
  color: string;
}

interface WorkloadData {
  workloads: WorkloadStats[];
  regionSummary: RegionSummary[];
  statusDistribution: StatusDistribution[];
}

interface WorkloadsReportProps {
  workloadData: WorkloadData;
}

const WorkloadsReport: React.FC<WorkloadsReportProps> = ({
  workloadData
}) => {
  return (
    <div className="space-y-6">
      {/* Workload Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Total Workloads</p>
          <p className="text-2xl font-bold text-gray-900">{workloadData.workloads.length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Total Homes</p>
          <p className="text-2xl font-bold text-gray-900">
            {workloadData.workloads.reduce((sum, w) => sum + w.totalAddresses, 0).toLocaleString()}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Completed</p>
          <p className="text-2xl font-bold text-green-600">
            {workloadData.workloads.reduce((sum, w) => sum + w.completedAddresses, 0).toLocaleString()}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Avg Completion</p>
          <p className="text-2xl font-bold text-indigo-600">
            {workloadData.workloads.length > 0
              ? Math.round(workloadData.workloads.reduce((sum, w) => sum + w.completionRate, 0) / workloadData.workloads.length)
              : 0}%
          </p>
        </div>
      </div>

      {/* Region Summary */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium mb-4">Region Summary</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Region</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Workloads</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Homes</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completion Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {workloadData.regionSummary.map((region, i) => (
                <tr key={i}>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{region.region}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{region.workloadCount}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{region.totalHomes.toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{region.completedHomes.toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      <div className="flex-1 h-2 bg-gray-200 rounded mr-2">
                        <div className="h-2 bg-green-500 rounded" style={{ width: `${region.completionRate}%` }} />
                      </div>
                      {region.completionRate}%
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Workload Details */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium mb-4">Workload Details</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Region</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Addresses</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {workloadData.workloads.map((wl, i) => (
                <tr key={i}>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{wl.name}</td>
                  <td className="px-6 py-4 text-sm">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      wl.status === 'active' ? 'bg-green-100 text-green-700' :
                      wl.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>{wl.status}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{wl.region}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{wl.totalAddresses}</td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex items-center">
                      <div className="flex-1 h-2 bg-gray-200 rounded mr-2 max-w-[100px]">
                        <div className="h-2 bg-green-500 rounded" style={{ width: `${wl.completionRate}%` }} />
                      </div>
                      {wl.completionRate}%
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WorkloadsReport;
